import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Download, CheckCircle2, Coffee, ShoppingBag, Store, Truck } from "lucide-react";
import { IMAGES, BRANDS } from "@/data/site";
import { api, asError } from "@/api/client";
import Reveal from "@/components/Reveal";

const PATHWAYS = [
  {
    code: "FR-01",
    title: "Unit Franchise",
    text: "Own and operate a single Millet Plaza, Stories or ProMill outlet in your city.",
    icon: Store,
    bestFor: "First-time food entrepreneurs",
  },
  {
    code: "FR-02",
    title: "Master Franchise",
    text: "Multi-unit territory rights across a city or region with operational support team.",
    icon: ShoppingBag,
    bestFor: "Experienced multi-outlet operators",
  },
  {
    code: "FR-03",
    title: "GoMill Kiosk",
    text: "Plug-and-play mobile kiosk for high-footfall locations: tech parks, campuses, residential clusters.",
    icon: Truck,
    bestFor: "Compact-format entrepreneurs",
  },
  {
    code: "FR-04",
    title: "Retail Distribution",
    text: "Stock Native Touch packaged products across modern trade, general trade and Q-commerce.",
    icon: Coffee,
    bestFor: "FMCG distributors & wholesalers",
  },
];

const SUPPORT = [
  ["Site Selection", "Footfall scoring · catchment analysis · negotiation."],
  ["Design & Build", "Architecture · interiors · vendor procurement."],
  ["Recipe & Training", "Central kitchen SOPs · 21-day chef bootcamp."],
  ["Supply Chain", "Single-origin millet supply · packaging · cold chain."],
  ["Marketing Launch", "Pre-buzz · influencer · performance marketing."],
  ["Ops Review", "Monthly business review · quarterly QC inspection."],
];

const REASONS = [
  "A proven millet-first food platform with 4 distinct brand formats",
  "Centralized R&D and supply chain you can lean on from day one",
  "Recognizable, design-led brands consumers already love",
  "End-to-end training, marketing and operational playbooks",
  "A partnerships team that listens before it sells",
];

export default function Franchise() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", pathway: "FR-01", background: "", message: "" });

  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { toast.error("Please fill required fields."); return; }
    setSubmitting(true);
    try {
      await api.post("/franchise", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city || "—",
        interest: `${form.pathway}${form.background ? " · " + form.background : ""}`,
        message: form.message,
      });
      toast.success("Enquiry received. Our partnerships team will be in touch within 48 hours.");
      setForm({ name: "", email: "", phone: "", city: "", pathway: "FR-01", background: "", message: "" });
    } catch (err) {
      toast.error(asError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-franchise" className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="pt-[110px] md:pt-[120px] pb-12 bg-[var(--bg)] relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:block opacity-90">
          <img src={IMAGES.gomillTruck} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[var(--bg)]" />
        </div>
        <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline overline-green">Partnership Gateway</div>
            <h1 className="font-serif-display text-4xl md:text-6xl mt-5 leading-tight text-[var(--ink)] font-semibold max-w-3xl">
              Build the Native Touch network <span className="text-[var(--gold)]">in your city.</span>
            </h1>
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-xl">
              Join India's most thoughtfully designed millet food platform. Multiple formats,
              one operational backbone, and a partnerships team that's obsessed with helping
              your unit thrive.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#enquire" data-testid="franchise-hero-enquire" className="btn-primary">Begin the conversation <ArrowRight size={14} /></a>
              <button type="button" onClick={() => toast.success("Brochure download coming soon.")} data-testid="franchise-hero-brochure" className="btn-outline">
                Download Brochure <Download size={14} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRAND STRIP */}
      <section className="py-8 border-y border-[var(--line-soft)] bg-white">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {BRANDS.map((b) => (
            <div key={b.slug} className="flex items-center gap-3">
              <div className="bg-[var(--bg-cream)] p-2 rounded-md border border-[var(--line-soft)]">
                <img src={b.logo} alt={b.name} className="h-10 w-10 object-contain" />
              </div>
              <div>
                <div className="font-serif-display text-base font-semibold text-[var(--ink)]">{b.name.split("—")[0].trim()}</div>
                <div className="kbd text-[var(--gold)] mt-0.5">{b.category}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl">
              <img src={IMAGES.milletPlazaDining} alt="" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="overline overline-green">Why Partner With Us</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              A partner, not just <span className="text-[var(--green)]">a licensor.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-2xl">
              We're building a long-term millet movement, not a quick rollout. Every conversation
              starts with what you bring to the table — and how we can help you do your best work.
            </p>
            <ul className="mt-7 space-y-3">
              {REASONS.map((r) => (
                <li key={r} className="flex items-start gap-3 text-[var(--ink-2)]">
                  <CheckCircle2 size={20} className="text-[var(--gold)] mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PATHWAYS */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">Pathways</div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                Four ways to <span className="text-[var(--green)]">join the table.</span>
              </h2>
              <p className="mt-5 text-[var(--ink-2)] max-w-xl mx-auto">
                Pick the format that matches your aspirations — every pathway is supported by the
                same operational backbone.
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {PATHWAYS.map((p, i) => (
              <Reveal key={p.code} delay={i * 0.06}>
                <div className="card-soft p-6 h-full flex flex-col">
                  <div className="flex items-center justify-between">
                    <span className="kbd text-[var(--gold)]">{p.code}</span>
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-cream)] flex items-center justify-center">
                      <p.icon size={20} className="text-[var(--green)]" />
                    </div>
                  </div>
                  <h3 className="font-serif-display text-xl md:text-2xl mt-5 leading-tight text-[var(--ink)] font-semibold">{p.title}</h3>
                  <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{p.text}</p>
                  <div className="mt-auto pt-5 border-t border-[var(--line-soft)]">
                    <div className="kbd text-[var(--ink-3)]">Best for</div>
                    <div className="text-sm text-[var(--green)] font-semibold mt-1.5">{p.bestFor}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <div className="overline overline-green">End-to-end Support</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              You don't open a unit. <span className="text-[var(--green)]">We open it with you.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-md">
              From site selection to ongoing business reviews — our partnerships team is
              structured to make sure your first unit is also your best.
            </p>
          </Reveal>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
            {SUPPORT.map(([t, d]) => (
              <div key={t} className="card-soft p-5">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 size={18} className="text-[var(--gold)]" />
                  <h4 className="font-serif-display text-lg font-semibold text-[var(--ink)]">{t}</h4>
                </div>
                <p className="text-sm text-[var(--ink-2)] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section bg-[var(--bg-soft)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <div className="overline overline-green">The Process</div>
              <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
                From first chat to <span className="text-[var(--green)]">first opening.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-12">
            {[
              ["01", "Apply", "Share your story, your city and what excites you about millets."],
              ["02", "Discover", "Exploratory conversation — we share the playbook, you share the vision."],
              ["03", "Align", "Choose the right format and territory. Sign the partnership."],
              ["04", "Launch", "We design, train, supply, market and stay close through the first 90 days."],
            ].map(([step, title, text], i) => (
              <Reveal key={step} delay={i * 0.06}>
                <div className="card-soft p-6 h-full">
                  <div className="font-serif-display text-5xl text-[var(--gold)] font-semibold leading-none">{step}</div>
                  <h3 className="font-serif-display text-xl mt-4 font-semibold text-[var(--ink)]">{title}</h3>
                  <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="enquire" className="section bg-[var(--bg)]">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-5">
            <div className="overline overline-green">Begin the conversation</div>
            <h2 className="font-serif-display text-3xl md:text-5xl mt-4 leading-tight text-[var(--ink)] font-semibold">
              Tell us about <span className="text-[var(--green)]">your city.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] leading-relaxed max-w-md">
              The first conversation is exploratory. We listen, share the playbook, and only
              proceed when there's a real fit on both sides.
            </p>
            <div className="mt-8 space-y-2 text-sm text-[var(--ink-2)]">
              <p>partnerships@nativetouchgroup.com</p>
              <p>+91 98220 00000</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="franchise-form" className="card-soft p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field testId="franchise-name" required label="Full name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field testId="franchise-email" required type="email" label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field testId="franchise-phone" required label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
              <Field testId="franchise-city" label="City of interest" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <div className="flex flex-col">
                <label className="kbd text-[var(--ink-3)] mb-2">Pathway</label>
                <select
                  data-testid="franchise-pathway"
                  value={form.pathway}
                  onChange={(e) => setForm({ ...form, pathway: e.target.value })}
                  className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
                >
                  {PATHWAYS.map((p) => (
                    <option key={p.code} value={p.code}>{`${p.code} — ${p.title}`}</option>
                  ))}
                </select>
              </div>
              <Field testId="franchise-background" label="Your background (food, retail, ops…)" value={form.background} onChange={(v) => setForm({ ...form, background: v })} />
              <div className="md:col-span-2 flex flex-col">
                <label className="kbd text-[var(--ink-3)] mb-2">Message</label>
                <textarea
                  data-testid="franchise-message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us about your background, timeline, and why this excites you."
                  className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap gap-3 pt-1">
                <button type="submit" data-testid="franchise-submit" className="btn-primary">Submit enquiry <ArrowRight size={14} /></button>
                <button type="button" onClick={() => toast.success("Brochure download coming soon.")} data-testid="franchise-form-brochure" className="btn-outline">
                  Download deck <Download size={14} />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ testId, label, value, onChange, required = false, type = "text" }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-2">{label}</label>
      <input
        data-testid={testId}
        required={required}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none"
      />
    </div>
  );
}
